import React, { useState, useRef, useEffect, FC, Dispatch, SetStateAction } from "react";
import { GoChevronDown } from "react-icons/go";
import { setCookie } from "../../lib/utils/cookies";
import { DEFAULT_COUNTRY_CODES, countryByCode } from "../../lib/utils/countryCodes";

interface PhoneNumberSelectProps {
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  // The hospital's codes (hospital_feature_flags.phone_country_codes). Omitted =
  // the original four.
  options?: string[];
}

// A search box appears once the list is longer than this.
const SEARCH_FROM = 6;

const PhoneNumberSelect: FC<PhoneNumberSelectProps> = ({ value, setValue, options }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const list = options && options.length ? options : DEFAULT_COUNTRY_CODES;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const handleChange = (selectedValue: string) => {
    setCookie("countryCode", selectedValue, 30);
    setValue(selectedValue);
    setOpen(false);
  };

  const q = query.trim().toLowerCase().replace(/^\+/, "");
  const shown = q
    ? list.filter((c) => {
        const info = countryByCode(c);
        return c.startsWith(q) || (info?.name || "").toLowerCase().includes(q) || (info?.short || "").toLowerCase().startsWith(q);
      })
    : list;

  return (
    // Sized to its content, never below the old 20%: a fixed 20% was narrower than
    // "IND +91 ⌄" on tablet widths, so the chevron sat on top of the code.
    <div ref={containerRef} className="relative inline-block shrink-0 min-w-[20%]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title={value ? countryByCode(value)?.name : undefined}
        className=" px-3 py-2  bg-transparent text-left flex items-center justify-between gap-1.5 w-full"
      >
        <span className="flex items-baseline gap-1.5 min-w-0 whitespace-nowrap">
          {/* Country short form on wider screens only; phones keep just "+91". */}
          {value && countryByCode(value)?.short && (
            <span className="hidden md:inline text-gray-500 text-sm">{countryByCode(value)?.short}</span>
          )}
          <span>+{value || "Select country code"}</span>
        </span>
        <GoChevronDown className="flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-10 left-0 w-full min-w-[120px] top-12 border border-sbBorder rounded-[5px] bg-white shadow-md">
          {list.length > SEARCH_FROM && (
            <div className="p-2 border-b border-sbBorder">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code"
                className="w-full px-2 py-1.5 text-sm border border-sbBorder rounded focus:outline-none focus:border-[#0F3CBE]"
              />
            </div>
          )}
          <ul className="max-h-[220px] overflow-y-auto side_bar">
            {shown.map((option) => (
              <li
                key={option}
                onClick={() => handleChange(option)}
                className={`px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-1.5 text-sm whitespace-nowrap ${
                  option === value ? "bg-[#EAF0FF]" : ""
                }`}
              >
                <span className="text-gray-700" title={countryByCode(option)?.name}>{countryByCode(option)?.short || "—"}</span>
                <span className="font-medium tabular-nums">+{option}</span>
              </li>
            ))}
            {shown.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">No match</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberSelect;
